import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Clock, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import { tourDates } from "@/data/mockData";
import heroImage from "@/assets/hero-concert.jpg";

const TourDetail = () => {
  const { id } = useParams();
  const show = tourDates.find((t) => t.id === id);

  if (!show) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-3xl font-bold text-foreground">Show not found</h1>
          <Link to="/tour" className="text-muted-foreground hover:text-foreground mt-4 inline-block">← Back to Tour</Link>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto">
        <Link to="/tour" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Tour
        </Link>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="lg:sticky lg:top-24 lg:self-start space-y-8 order-2 lg:order-1">
            <div>
              <p className="text-6xl font-bold text-foreground mb-1">{show.date}</p>
              <p className="text-muted-foreground text-lg uppercase tracking-wide">{show.month}</p>
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-2">{show.eventName}</h1>
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span>{show.venue} — {show.city}, {show.state}</span>
              </div>
            </div>

            {(show.doors || show.showTime) && (
              <div className="flex gap-6">
                {show.doors && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">Doors: {show.doors}</span>
                  </div>
                )}
                {show.showTime && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">Show: {show.showTime}</span>
                  </div>
                )}
              </div>
            )}

            {show.description && (
              <p className="text-muted-foreground leading-relaxed">{show.description}</p>
            )}

            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-widest mb-4">Attend</h3>
              <div className="space-y-3">
                <a href="#" className="flex items-center justify-between glass-card-hover p-4 group">
                  <span className="text-foreground font-medium">Ticketmaster</span>
                  <div className="flex items-center gap-3">
                    <span className="text-muted-foreground text-sm font-semibold">{show.price}</span>
                    <ExternalLink className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </div>
                </a>
                <a href="#" className="flex items-center justify-between glass-card-hover p-4 group">
                  <span className="text-foreground font-medium">Venue Box Office</span>
                  <div className="flex items-center gap-3">
                    <span className="text-muted-foreground text-sm font-semibold">{show.price}</span>
                    <ExternalLink className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </div>
                </a>
              </div>
            </div>
          </div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="order-1 lg:order-2">
            <div className="aspect-[3/4] rounded-lg overflow-hidden">
              <img src={heroImage} alt={show.eventName} className="w-full h-full object-cover" />
            </div>
          </motion.div>
        </div>
      </div>
    </PageLayout>
  );
};

export default TourDetail;
